import React, { useContext, useState, useCallback, useMemo } from "react";
import { fetchClientHealthData } from "../services/GWHealthCheckRestHandler";

const HealthCheckDataContext = React.createContext();
const ChangeHealthCheckDataContext = React.createContext();

const DataToVisualizeContext = React.createContext();
const ChangeDataToVisualizeContext = React.createContext();

const StatisticContext = React.createContext();
const ChangeStatisticContext = React.createContext();

const ModuleContext = React.createContext();
const ChangeModuleContext = React.createContext();

const ClientContext = React.createContext();
const ChangeClientContext = React.createContext();

const ClientsHealthContext = React.createContext();
const RefreshClientsHealthContext = React.createContext();

export function useHealthCheckData() {
    return useContext(HealthCheckDataContext);
}

export function useChangeHealthCheckData() {
    return useContext(ChangeHealthCheckDataContext);
}

export function useDataToVisualize() {
    return useContext(DataToVisualizeContext);
}

export function useChangeDataToVisualize() {
    return useContext(ChangeDataToVisualizeContext);
}

export function useStatistic() {
    return useContext(StatisticContext);
}

export function useChangeStatistic() {
    return useContext(ChangeStatisticContext);
}

export function useModule() {
    return useContext(ModuleContext);
}

export function useChangeModule() {
    return useContext(ChangeModuleContext);
}

export function useClient() {
    return useContext(ClientContext);
}

export function useChangeClient() {
    return useContext(ChangeClientContext);
}

export function useClientsHealth() {
    return useContext(ClientsHealthContext);
}

export function useRefreshClientsHealth() {
    return useContext(RefreshClientsHealthContext);
}

export default function HealthCheckProvider({ children }) {
    const [healtCheckData, setHealthCheckData] = useState("");
    const [dataToVisualize, setDataToVisualize] = useState("");
    const [statistic, setStatistic] = useState("");
    const [module, setModule] = useState("");
    const [client, setClient] = useState("ClientName");

    const [clientsHealth, setClientsHealth] = useState({});
    const [clientsLoading, setClientsLoading] = useState({});
    const [clientsError, setClientsError] = useState({});

    // Memoize clients array to avoid changing on every render
    const clients = useMemo(() => ["ClientA", "ClientB", "ClientC", "ClientD", "ClientE"], []);

    function changeHealthCheckData(data) {
        setHealthCheckData(data);
    }

    function changeDataToVisualize(data) {
        setDataToVisualize(data);
    }

    function changeStatistic(statistic) {
        setStatistic(statistic);
    }

    function changeModule(module) {
        setModule(module);
    }

    function changeClient(client) {
        setClient(client);
    }

    // Wrap fetchAllClientsHealth in useCallback to prevent it from changing on every render
    const fetchAllClientsHealth = useCallback(async () => {
        const loadingState = {};
        const errorState = {};
        const dataState = {};
        clients.forEach(client => { loadingState[client] = true; errorState[client] = null; });
        setClientsLoading(loadingState);
        setClientsError(errorState);
        setClientsHealth({});
        await Promise.all(clients.map(async (client) => {
            try {
                const data = await fetchClientHealthData(client);
                dataState[client] = data;
            } catch (err) {
                errorState[client] = err.message;
            } finally {
                loadingState[client] = false;
            }
        }));
        setClientsHealth(dataState);
        setClientsLoading({ ...loadingState });
        setClientsError({ ...errorState });
    }, [clients]);

    React.useEffect(() => {
        fetchAllClientsHealth();
    }, [fetchAllClientsHealth]);

    return (
        <ClientsHealthContext.Provider value={{ clientsHealth, clientsLoading, clientsError }}>
            <RefreshClientsHealthContext.Provider value={fetchAllClientsHealth}>
                <HealthCheckDataContext.Provider value={healtCheckData}>
                    <ChangeHealthCheckDataContext.Provider value={changeHealthCheckData}>
                        <DataToVisualizeContext.Provider value={dataToVisualize}>
                            <ChangeDataToVisualizeContext.Provider value={changeDataToVisualize}>
                                <StatisticContext.Provider value={statistic}>
                                    <ChangeStatisticContext.Provider value={changeStatistic}>
                                        <ModuleContext.Provider value={module}>
                                            <ChangeModuleContext.Provider value={changeModule}>
                                                <ClientContext.Provider value={client}>
                                                    <ChangeClientContext.Provider value={changeClient}>
                                                        {children}
                                                    </ChangeClientContext.Provider>
                                                </ClientContext.Provider>
                                            </ChangeModuleContext.Provider>
                                        </ModuleContext.Provider>
                                    </ChangeStatisticContext.Provider>
                                </StatisticContext.Provider>
                            </ChangeDataToVisualizeContext.Provider>
                        </DataToVisualizeContext.Provider>
                    </ChangeHealthCheckDataContext.Provider>
                </HealthCheckDataContext.Provider>
            </RefreshClientsHealthContext.Provider>
        </ClientsHealthContext.Provider>
    );
}
